import type {
  Promotion,
  PromotionInput,
  PromotionStatus,
} from '../../types/businessProfile.js';

// Promotions — which ones the bot may talk about RIGHT NOW. PURE.
//
// ⚠️⚠️ THIS IS PRINCIPLE 1, "THE MODEL CAN'T USE WHAT IT NEVER SEES". An expired
// or scheduled campaign is filtered HERE, server-side, and never reaches the
// prompt — so "when a promo expires the bot never mentions it" is true by
// construction rather than by instruction. A request is an instruction the model
// may or may not follow; a missing block is structural.
//
// ⚠️ DATES ARE LOCAL CALENDAR DATES (YYYY-MM-DD), INCLUSIVE, IN THE CLUB'S
// TIMEZONE. The UTC date and the club's local date disagree for ~10-13 hours of
// every day in AU/NZ, so comparing against the UTC date would end a Brisbane
// campaign at 10am on its last day. Everything goes through `localDateString`.

const YMD = /^\d{4}-\d{2}-\d{2}$/;

/** The calendar date at `nowMs` in `timezone`, as YYYY-MM-DD. */
export function localDateString(nowMs: number, timezone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date(nowMs));
    const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
    const y = get('year');
    const m = get('month');
    const d = get('day');
    if (y === '' || m === '' || d === '') throw new Error('unreadable parts');
    return `${y}-${m}-${d}`;
  } catch {
    // A bad zone degrades to the UTC date rather than throwing inside a send path.
    return new Date(nowMs).toISOString().slice(0, 10);
  }
}

export function isValidYmd(value: string | null | undefined): value is string {
  if (typeof value !== 'string' || !YMD.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number) as [number, number, number];
  const probe = new Date(Date.UTC(y, m - 1, d));
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d;
}

type PromoDates = Pick<Promotion, 'kind' | 'startDate' | 'endDate' | 'enabled'>;

export function promotionStatus(
  promo: PromoDates,
  nowMs: number,
  timezone: string,
): PromotionStatus {
  if (!promo.enabled) return 'disabled';
  if (promo.kind === 'always_on') return 'live';
  // ⚠️ A campaign with an unreadable date is NEVER live. Failing closed: showing
  // an offer the club may have ended is the damaging direction.
  if (!isValidYmd(promo.startDate) || !isValidYmd(promo.endDate)) return 'expired';
  const today = localDateString(nowMs, timezone);
  if (today < promo.startDate) return 'scheduled';
  if (today > promo.endDate) return 'expired';
  return 'live';
}

/** A campaign that ended within this many days may be NAMED to the model (title only). */
export const ENDED_TITLE_WINDOW_DAYS = 60;

/**
 * Titles (and ONLY titles) of campaigns that ended within the last 60 days, so Sam can say "that one has
 * finished" when a lead asks about it by name. ⚠️ No wording, terms or figures ever travel with it:
 * the model still cannot quote the old deal. Unreadable dates are excluded (fail closed).
 */
export function recentlyEndedTitles<T extends PromoDates & { title: string }>(
  promos: readonly T[],
  nowMs: number,
  timezone: string,
): string[] {
  const today = localDateString(nowMs, timezone);
  const cutoff = localDateString(nowMs - ENDED_TITLE_WINDOW_DAYS * 24 * 3600_000, timezone);
  return promos
    .filter(
      (p) =>
        p.kind === 'campaign' &&
        p.enabled &&
        isValidYmd(p.endDate) &&
        p.endDate! < today &&
        p.endDate! >= cutoff &&
        p.title.trim() !== '',
    )
    .map((p) => p.title.trim());
}

/**
 * The promotions the model may be shown. Always-on offers that are enabled, plus
 * campaigns that are live today at the club. ⚠️ Expired, scheduled and disabled
 * ones are absent — the only way they could reach the prompt is through a bug in
 * this one function, which is why its tests carry a deleted-filter negative
 * control.
 */
export function activePromotions<T extends PromoDates>(
  promos: readonly T[],
  nowMs: number,
  timezone: string,
): T[] {
  return promos.filter((p) => promotionStatus(p, nowMs, timezone) === 'live');
}

/** Live campaigns first (the bot may lead with one), always-on after. */
export function orderForPrompt<T extends PromoDates>(promos: readonly T[]): T[] {
  const rank = (p: T): number => (p.kind === 'campaign' ? 0 : 1);
  return [...promos].sort((a, b) => rank(a) - rank(b));
}

/**
 * Validate a promotion's shape. Returns the first problem, or null.
 * ⚠️ A campaign REQUIRES both dates and an end on/after the start; an always-on
 * offer FORBIDS them (a dated "always-on" is a campaign wearing a disguise).
 */
export function promotionError(input: PromotionInput): string | null {
  if (input.title.trim() === '') return 'Give the promotion a title.';
  if (input.exactWording.trim() === '') return 'Write the exact wording the assistant may say.';
  if (input.kind === 'always_on') {
    if (input.startDate !== null || input.endDate !== null) {
      return 'An always-on offer has no dates. Make it a campaign if it ends.';
    }
    return null;
  }
  if (!isValidYmd(input.startDate) || !isValidYmd(input.endDate)) {
    return 'A campaign needs a start and an end date.';
  }
  if (input.endDate < input.startDate) return 'The end date is before the start date.';
  return null;
}

export const PROMOTION_STATUS_LABELS: Record<PromotionStatus, string> = {
  scheduled: 'Scheduled',
  live: 'Live',
  expired: 'Expired',
  disabled: 'Disabled',
};
